/**
 * Scope 2 Comparison Card
 *
 * Shows location-based and market-based Scope 2 totals side-by-side
 * per GHG Protocol dual reporting requirements. Clicking a column
 * selects which method the downstream breakdowns reflect.
 */

import { Loader2, MapPin, ShoppingCart, TrendingDown, Info, AlertTriangle, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Progress as ProgressPrimitive } from 'radix-ui';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { Scope2Method } from '../types/emissions';
import { useMemo } from 'react';

interface Scope2ComparisonCardProps {
  locationBasedTotal: number;
  marketBasedTotal: number;
  totalEnergyConsumption: number;
  marketBasedActivitiesCount: number;
  isLoading: boolean;
  activeMethod?: Scope2Method;
}

const formatEnergy = (kwh: number): string => {
  if (kwh >= 1_000_000) return `${(kwh / 1_000_000).toFixed(1)}M`;
  if (kwh >= 1_000) return `${(kwh / 1_000).toFixed(1)}k`;
  return kwh.toFixed(0);
};

export const Scope2ComparisonCard = ({
  locationBasedTotal,
  marketBasedTotal,
  totalEnergyConsumption,
  marketBasedActivitiesCount,
  isLoading,
  activeMethod,
}: Scope2ComparisonCardProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const hasMarketData = marketBasedActivitiesCount > 0;
  const marketSavings = locationBasedTotal - marketBasedTotal;
  const savingsPercentage = locationBasedTotal > 0
    ? Math.round((marketSavings / locationBasedTotal) * 100)
    : 0;

  const locationFormatted = formatCompact(locationBasedTotal);
  const marketFormatted = formatCompact(marketBasedTotal);
  const savingsFormatted = formatCompact(Math.abs(marketSavings));

  const avgGridEmissionFactor = useMemo(() => {
    if (totalEnergyConsumption === 0 || locationBasedTotal === 0) return null;
    return (locationBasedTotal / totalEnergyConsumption) * 1000;
  }, [locationBasedTotal, totalEnergyConsumption]);

  const renewablePercentage = useMemo(() => {
    if (!locationBasedTotal || locationBasedTotal === 0 || !Number.isFinite(marketSavings)) return 0;
    const percentage = Math.round((marketSavings / locationBasedTotal) * 100);
    return Number.isFinite(percentage) ? percentage : 0;
  }, [locationBasedTotal, marketSavings]);

  return (
    <Card className='tw:mb-6'>
      <CardHeader className='tw:pb-2'>
        <CardTitle className='tw:text-base tw:font-semibold'>GHG Protocol Dual Reporting</CardTitle>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
          Compare location-based and market-based accounting methods
        </p>
      </CardHeader>
      <CardContent>
        <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4'>
          {/* Location-Based Column */}
          <div className={cn(
            'tw:rounded-lg tw:border tw:border-border tw:border-l-4 tw:border-l-blue-400 tw:bg-card tw:shadow-sm tw:p-4',
            activeMethod === Scope2Method.LocationBased && 'tw:ring-2 tw:ring-blue-400/50',
          )}>
            <div className='tw:flex tw:items-center tw:gap-2 tw:mb-3'>
              <div className='tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:size-8 tw:bg-blue-500/10'>
                <MapPin className='tw:size-4 tw:text-blue-600' />
              </div>
              <span className='tw:text-sm tw:font-medium'>Location-Based</span>
            </div>
            {isLoading ? (
              <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
            ) : (
              <>
                <div className='tw:flex tw:items-baseline tw:gap-1 tw:mb-3'>
                  <span className='tw:text-2xl tw:font-bold tw:text-blue-400'>
                    {locationFormatted.formatted}
                  </span>
                  <span className='tw:text-sm tw:text-muted-foreground'>{locationFormatted.unit}</span>
                </div>
                <div className='tw:text-xs tw:text-muted-foreground tw:mb-1'>Based on grid average emission factors</div>
                <div className='tw:space-y-1 tw:mt-3 tw:pt-3 tw:border-t tw:border-border'>
                  <div className='tw:flex tw:justify-between tw:text-xs'>
                    <span className='tw:text-muted-foreground'>Energy Consumption</span>
                    <span className='tw:font-medium'>{formatEnergy(totalEnergyConsumption)} kWh</span>
                  </div>
                  <div className='tw:flex tw:justify-between tw:text-xs'>
                    <span className='tw:text-muted-foreground'>Grid Emission Factor</span>
                    <span className='tw:font-medium'>
                      {avgGridEmissionFactor !== null ? `${avgGridEmissionFactor.toFixed(2)} kgCO₂e/kWh` : '--'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Market-Based Column */}
          <div className={cn(
            'tw:rounded-lg tw:border tw:border-border tw:border-l-4 tw:border-l-emerald-500 tw:bg-card tw:shadow-sm tw:p-4',
            activeMethod === Scope2Method.MarketBased && 'tw:ring-2 tw:ring-emerald-500/50',
          )}>
            <div className='tw:flex tw:items-center tw:gap-2 tw:mb-3'>
              <div className='tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:size-8 tw:bg-emerald-500/10'>
                <ShoppingCart className='tw:size-4 tw:text-emerald-600' />
              </div>
              <span className='tw:text-sm tw:font-medium'>Market-Based</span>
            </div>
            {isLoading ? (
              <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
            ) : !hasMarketData ? (
              <>
                <div className='tw:flex tw:items-baseline tw:gap-1 tw:mb-3'>
                  <span className='tw:text-2xl tw:font-bold tw:text-muted-foreground'>
                    {locationFormatted.formatted}
                  </span>
                  <span className='tw:text-sm tw:text-muted-foreground'>{locationFormatted.unit}</span>
                </div>
                <div className='tw:flex tw:gap-2 tw:rounded tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-2.5 tw:text-blue-800 tw:text-xs'>
                  <Info className='tw:size-3.5 tw:shrink-0 tw:mt-0.5' />
                  <div>
                    No market-based instruments configured — defaulting to location-based.
                    Run a dual calculation with RECs, PPAs, or supplier EFs to see the difference.
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className='tw:flex tw:items-baseline tw:gap-1 tw:mb-3'>
                  <span className='tw:text-2xl tw:font-bold tw:text-emerald-600'>
                    {marketFormatted.formatted}
                  </span>
                  <span className='tw:text-sm tw:text-muted-foreground'>{marketFormatted.unit}</span>
                </div>
                <div className='tw:text-xs tw:text-muted-foreground tw:mb-1'>Based on contractual instruments</div>
                <div className='tw:space-y-1 tw:mt-3 tw:pt-3 tw:border-t tw:border-border'>
                  <div className='tw:flex tw:justify-between tw:text-xs'>
                    <span className='tw:text-muted-foreground'>Renewable Energy</span>
                    <span className={`tw:text-md ${renewablePercentage > 0 ? 'tw:text-emerald-600' : 'tw:text-destructive'}`}>
                      {renewablePercentage}%
                      {renewablePercentage > 0 ? <Check className='tw:size-4' /> : <AlertTriangle className='tw:size-4' />}
                    </span>
                  </div>
                  <div className='tw:flex tw:justify-between tw:text-xs'>
                    <span className='tw:text-muted-foreground'>Supplier EFs Used</span>
                    <span className='tw:font-medium'>
                      {marketBasedActivitiesCount} activit{marketBasedActivitiesCount !== 1 ? 'ies' : 'y'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Delta Row — only shown when market data exists */}
        {!isLoading && hasMarketData && (
          <div className='tw:rounded-lg tw:bg-muted/50 tw:p-3'>
            {marketSavings > 0 ? (
              <div>
                <div className='tw:flex tw:items-center tw:gap-2 tw:mb-2'>
                  <TrendingDown className='tw:size-4 tw:text-emerald-600' />
                  <span className='tw:text-sm tw:font-medium'>
                    Market-based is {savingsPercentage}% lower
                  </span>
                  <span className='tw:text-sm tw:text-muted-foreground'>
                    (-{savingsFormatted.formatted} {savingsFormatted.unit})
                  </span>
                </div>
                <ProgressPrimitive.Root
                  value={100}
                  className='tw:relative tw:h-2 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-blue-400'
                >
                  <ProgressPrimitive.Indicator
                    className='tw:h-full tw:bg-emerald-500 tw:transition-all'
                    style={{ width: `${Math.min(100 - renewablePercentage, 100)}%` }}
                  />
                </ProgressPrimitive.Root>
                <div className='tw:flex tw:justify-between tw:mt-1'>
                  <span className='tw:text-xs tw:text-muted-foreground'>SUCCESS: Renewable coverage</span>
                  <span className='tw:text-xs tw:font-medium tw:text-emerald-600'>{renewablePercentage}%</span>
                </div>
              </div>
            ) : marketSavings < 0 ? (
              <div>
                <div className='tw:flex tw:items-center tw:gap-2 tw:mb-2'>
                  <TrendingDown className='tw:size-4 tw:text-destructive tw:shrink-0' />
                  <span className='tw:text-sm tw:font-medium'>
                    Market-based is {Math.abs(savingsPercentage)}% higher
                  </span>
                  <span className='tw:text-sm tw:text-muted-foreground'>
                    (+{savingsFormatted.formatted} {savingsFormatted.unit})
                  </span>
                </div>
                <ProgressPrimitive.Root
                  value={100}
                  className='tw:relative tw:h-2 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-destructive'
                >
                  <ProgressPrimitive.Indicator
                    className='tw:h-full tw:bg-blue-400 tw:transition-all'
                    style={{ width: `${Math.min(100 + renewablePercentage, 100)}%` }}
                  />
                </ProgressPrimitive.Root>
                <div className='tw:flex tw:justify-between tw:mt-1'>
                  <span className='tw:text-xs tw:text-muted-foreground'>
                    <AlertTriangle className='tw:size-4 tw:text-destructive' /> WARNING: Residual mix factor exceeds grid average
                  </span>
                  <span className='tw:text-xs tw:font-medium tw:text-destructive'>{Math.abs(savingsPercentage)}%</span>
                </div>
              </div>
            ) : (
              <div className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground'>
                <Info className='tw:size-4' />
                <span>Location-based and market-based report identical emissions</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
